import React from "react";
import { Field, Form, Formik } from "formik";
import Col from "react-bootstrap/lib/Col";
import Grid from "react-bootstrap/lib/Grid";
import Row from "react-bootstrap/lib/Row";

import { actions as settingsActions } from "reducers/settings";
import * as config from "util/config";
import * as i18n from "util/i18n";
import * as Yup from "yup";

import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import Credential from "pages/_components/fields/credentials/Credential";

import ToDoList from "pages/onboarding/_components/ToDoList";
import { useDispatch } from "react-redux";
import { getItems, getNotContainItems, getMinMax, passwordStrength, submitIsDisabled } from "util/password";
import Box from "pages/_components/Box";

const FORM_ID = "settings.changePassword";

const ChangePassword = () => {
    const dispatch = useDispatch();
    const { maxLength, maxLengthValidationMsg, minLength, minLengthValidationMsg } = getMinMax();

    const initialValues = {
        password: "",
        newPassword: "",
        newPasswordConfirmation: "",
    };

    const validationSchema = Yup.lazy((values) =>
        Yup.object().shape({
            password: Yup.string().required(i18n.get(`${FORM_ID}.password.required`)),
            newPassword: Yup.string()
                .required(i18n.get("enrollment.step3.password.error.empty"))
                .min(minLength, minLengthValidationMsg)
                .max(maxLength, maxLengthValidationMsg)
                .matches(
                    new RegExp(config.get("core.userconfiguration.password.pattern"), "g"),
                    i18n.get("enrollment.step3.password.error.unfulfilledRules"),
                )
                .oneOf(
                    [values.newPasswordConfirmation],
                    i18n.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                ),
            newPasswordConfirmation: Yup.string()
                .matches(
                    new RegExp(config.get("core.userconfiguration.password.pattern"), "g"),
                    i18n.get("enrollment.step3.password.error.unfulfilledRules"),
                )
                .oneOf([values.newPassword], i18n.get("enrollment.step3.password.error.passwordConfirmationMustMatch")),
        }),
    );

    const handleSubmit = (values, formikBag) => {
        dispatch(
            settingsActions.changePassword(
                values.password,
                values.newPassword,
                values.newPasswordConfirmation,
                formikBag,
            ),
        );
    };

    const firstValidation = {
        description: minLengthValidationMsg,
        status: (password) => {
            if (!password) {
                return "INITIAL";
            }
            return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
        },
    };

    const getFirstItem = (password) => ({
        status: firstValidation.status(password),
        description: firstValidation.description,
    });

    return (
        <>
            <Notification scopeToShow="changePassword" />
            <ResponsiveHeader id="settings.changePassword" backLinkTo="/settings" />
            <MainContainer>
                <Formik
                    validateOnChange
                    validateOnBlur
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}>
                    {({ values, isSubmitting }) => (
                        <Form className="above-the-fold">
                            <section className="container--layout flex-grow items-center box box-shadow pt-8 pb-10 px-6">
                                <Grid className="form-content">
                                    <Row className="justify-content-center">
                                        <Col xs={12} md={10} mdOffset={1}>
                                            <p className="font-size-md mb-8">
                                                <I18n id="settings.changePassword.textInfo" />
                                            </p>
                                            <Field
                                                name="password"
                                                idForm={FORM_ID}
                                                component={Credential}
                                                maxLength={maxLength}
                                            />
                                            <Field
                                                name="newPassword"
                                                idForm={FORM_ID}
                                                component={Credential}
                                                showStrength
                                                strength={passwordStrength(values?.newPassword)}
                                                maxLength={maxLength}
                                            />
                                            <Box>
                                                <ToDoList items={[getFirstItem(values?.newPassword)]} />
                                                <ToDoList items={getItems(values?.newPassword)} />
                                            </Box>
                                            <p className="font-size-md mt-8">
                                                <I18n
                                                    id="onboarding.password.notContainValidations"
                                                    defaultValue="No debe contener:"
                                                />
                                            </p>
                                            <Box className="mb-9">
                                                <ToDoList items={getNotContainItems(values?.newPassword)} />
                                            </Box>
                                            <Field
                                                name="newPasswordConfirmation"
                                                idForm={FORM_ID}
                                                component={Credential}
                                                maxLength={maxLength}
                                            />
                                        </Col>
                                    </Row>
                                </Grid>
                            </section>
                            <section className="container--layout items-center mb-6">
                                <Grid className="form-content">
                                    <Row className="justify-content-center">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                label="global.modify"
                                                loading={isSubmitting}
                                                type="submit"
                                                bsStyle="primary"
                                                disabled={submitIsDisabled({
                                                    firstValidation,
                                                    password: values?.newPassword,
                                                    repeatedPassword: values?.newPasswordConfirmation,
                                                })}
                                            />
                                        </Col>
                                    </Row>
                                </Grid>
                            </section>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </>
    );
};

export default ChangePassword;
